<template>
  <div class="relative inline-flex shrink-0 rounded-full">
    <Avatar
      :class="['ring-1 ring-inset ring-border/60 shadow-sm', sizeClasses]"
    >
      <AvatarImage v-if="src" :src="src" :alt="name" />
      <AvatarFallback
        :class="['font-semibold text-white', fontSizeClass]"
        :style="{ background: gradient }"
      >
        <span class="drop-shadow-sm leading-none">{{ initials }}</span>
      </AvatarFallback>
    </Avatar>
    <span
      v-if="statusDot"
      class="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-card"
      :class="statusDot"
    />
  </div>
</template>

<script>
import { Avatar, AvatarImage, AvatarFallback } from "@/shared/ui/avatar";

function hashString(str) {
  let h = 2166136261;
  const s = String(str || "");
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

// HSL дает 360 различимых hue вместо 11 — резко снижаем коллизии при
// больших списках. Saturation/lightness фиксированы для читаемости
// белого текста на фоне.
function gradientFor(seed) {
  const hue = hashString(seed) % 360;
  const top = `hsl(${hue}, 55%, 60%)`;
  const bottom = `hsl(${hue}, 60%, 42%)`;
  return `linear-gradient(180deg, ${top} 0%, ${bottom} 100%)`;
}

function initialsFromName(name) {
  if (!name) return "";
  return String(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

export default {
  name: "UserAvatar",
  components: { Avatar, AvatarImage, AvatarFallback },
  props: {
    // ИИН пользователя — приоритетный источник стабильного seed.
    identifier: { type: String, default: "" },
    // Запасной seed (uuid / person_id) если ИИН недоступен.
    seed: { type: String, default: "" },
    name: { type: String, default: "" },
    src: { type: String, default: "" },
    status: { type: String, default: null },
    size: { type: Number, default: 36 },
  },
  computed: {
    initials() {
      return initialsFromName(this.name);
    },
    // Цвет привязан только к первым двум словам имени (фамилия + имя).
    // ИИН/uuid/person_id игнорируем — у дубликатов одной персоны они разные,
    // но фамилия+имя одинаковые → стабильный цвет per человек.
    effectiveSeed() {
      const fromName = String(this.name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .join(" ")
        .toLowerCase();
      return fromName || String(this.identifier || this.seed || "").trim();
    },
    gradient() {
      return gradientFor(this.effectiveSeed);
    },
    sizeClasses() {
      if (this.size <= 24) return "size-6";
      if (this.size <= 28) return "size-7";
      if (this.size <= 32) return "size-8";
      if (this.size <= 40) return "size-9";
      if (this.size <= 48) return "size-12";
      if (this.size <= 56) return "size-14";
      if (this.size <= 64) return "h-16 w-16";
      if (this.size <= 72) return "h-[72px] w-[72px]";
      if (this.size <= 88) return "h-[88px] w-[88px]";
      return "h-24 w-24";
    },
    fontSizeClass() {
      if (this.size <= 24) return "text-[10px]";
      if (this.size <= 28) return "text-[11px]";
      if (this.size <= 32) return "text-xs";
      if (this.size <= 40) return "text-[13px]";
      if (this.size <= 48) return "text-sm";
      if (this.size <= 56) return "text-base";
      if (this.size <= 64) return "text-lg";
      if (this.size <= 72) return "text-xl";
      if (this.size <= 88) return "text-2xl";
      return "text-3xl";
    },
    statusDot() {
      if (!this.status) return null;
      if (this.status === "active") return "bg-green-500";
      if (this.status === "review") return "bg-orange-500";
      if (this.status === "locked") return "bg-red-500";
      return null;
    },
  },
};
</script>
